export const accidentals = ['bb', 'b', '', '#', 'x'] as const;
export type Accidental = typeof accidentals[number];

export function isAccidental(accidental: string): accidental is Accidental {
  return accidentals.includes(accidental as Accidental);
}

/**
 * Dictionary of half-steps by which an accidental modifies a note.
 */
export const accidentalIntegers: Record<Accidental, number> = {
  x: 2,
  '#': 1,
  '': 0,
  b: -1,
  bb: -2,
};

/**
 * Dictionary of font codes for accidental strings.
 */
export const accidentalFontCodes: Record<Accidental, string> = {
  x: 'x',
  '#': '#',
  '': 'n',
  b: 'b',
  bb: 'v',
};

export const letterNames = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
export type LetterName = typeof letterNames[number];

// We type this so that the template type NoteName is possible -- if the
// type were just number, that template type would become any. However,
// through the code base, any integer is valid.
export type OctaveNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type NoteName = `${LetterName}${Accidental}${OctaveNumber | ''}`;
export type ShortNoteName = `${LetterName}${Accidental}`;

const accidentalsReString = `(${accidentals.filter((n) => n).join('|')})?`;
const letterNamesReString = `(${letterNames.join('|')})`;
const octaveReString = `(\\d+)?`;
const noteNameRe = new RegExp(`^${letterNamesReString}${accidentalsReString}${octaveReString}$`);
export function isNoteName(noteName: any): noteName is NoteName {
  return !!noteNameRe.exec(noteName);
}

export interface NoteProperties {
  letter: LetterName;
  accidental: Accidental;
  octave: number;
}

export function isLetterName(letterName: any): letterName is LetterName {
  return letterNames.includes(letterName as LetterName);
}

/**
 * Map of letter name to integer, where C is 0, D is 1, etc...
 */
export const noteLetterIntegers: Record<LetterName, number> = {
  C: 0,
  D: 1,
  E: 2,
  F: 3,
  G: 4,
  A: 5,
  B: 6,
};

export const pitchClasses = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;
export type PitchClass = typeof pitchClasses[number];

/**
 * Map of letter names to Forte pitch classes
 */
export const noteLetterPitchClasses: Record<LetterName, PitchClass> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

/**
 * Most common lettername spelling of each midi number.
 */
export const midiNumbersBasic: Record<PitchClass, NoteName> = {
  0: 'C',
  1: 'C#',
  2: 'D',
  3: 'Eb',
  4: 'E',
  5: 'F',
  6: 'F#',
  7: 'G',
  8: 'Ab',
  9: 'A',
  10: 'Bb',
  11: 'B',
};

export const midiFlats = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'] as const;
export const midiSharps = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

export function getPianoScreenReaderNamesFromMidi(midi: number): string {
  const description = [
    'white key left of two black keys', // C
    'first of the two black keys',
    'white key between two black keys', // D
    'second of the two black keys',
    'white key right of two black keys', // E
    'white key left of three black keys', // F
    'leftmost of the three black keys',
    'white key left of the center of the three black keys', // G
    'middle black key of the three black keys',
    'white key right of the center of the three black keys', // A
    'rightmost of the three black keys',
    'white key right of three black keys', // B
  ][midi % 12];

  if (!description) {
    throw Error(`Midi must be a positive integer, not ${midi}, to get a screen reader description`);
  }

  return description;
}

export function getNoteNamesFromMidi(midi: number) {
  const midiNames = [
    'C',
    'C#/Db',
    'D',
    'D#/Eb',
    'E',
    'F',
    'F#/Gb',
    'G',
    'G#/Ab',
    'A',
    'A#/Bb',
    'B',
  ] as const;
  return midiNames[midi % 12];
}
